import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type UseFormSetError } from 'react-hook-form';
import {
  useConfigurableTypes,
  useCreateConfigurableType,
  useUpdateConfigurableType,
  useDeleteConfigurableType,
  useConfigurableTypeById,
} from '../../../hooks/queries/useReunification';
import type {
  ConfigurableType,
  ConfigurableTypeFormValues,
  ConfigurableTypeWrite,
  TypeCategory,
} from '../../../types/ReunificationTypes';
import { ConfigurableTypeInitialValues } from '../../../types/ReunificationTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import type { Column, StatusType } from '@themes/aegix';
import { useSearchParams } from 'react-router-dom';
import { getMatchedTab } from '../TypesContent';

export interface TypeRow {
  [key: string]: unknown;
  id: string;
  name: string;
  code: string;
  description: string;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
  system_status: StatusType;
  active_status: StatusType;
}

function transformTypes(types: ConfigurableType[]): TypeRow[] {
  return types.map((t) => ({
    id: t.public_id,
    name: t.name,
    code: t.code,
    description: t.description,
    is_system: t.is_system,
    is_active: t.is_active,
    sort_order: t.sort_order,
    system_status: t.is_system
      ? ('info' as StatusType)
      : ('default' as StatusType),
    active_status: t.is_active
      ? ('active' as StatusType)
      : ('inactive' as StatusType),
  }));
}

export function useConfigurableTypeCrud(category: TypeCategory) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('public_id');
  const urlTab = searchParams.get('subtab');
  const isCurrentTab = !urlTab || getMatchedTab(urlTab) === category;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [actionMode, setActionMode] = useState<'add' | 'edit' | ''>('');
  const [currentRow, setCurrentRow] = useState<TypeRow>();
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [systemFilter, setSystemFilter] = useState<string>('all');

  const categoryLabels: Record<TypeCategory, string> = useMemo(
    () => ({
      'station-types': t('admin.category.station', 'Station'),
      'zone-types': t('admin.category.zone', 'Zone'),
      'staff-role-types': t('admin.category.staffRole', 'Staff Role'),
      'label-types': t('admin.category.signage', 'Signage'),
      'note-types': t('admin.category.noteType', 'Note Type'),
    }),
    [t],
  );

  const createMutation = useCreateConfigurableType(category);
  const updateMutation = useUpdateConfigurableType(category);
  const deleteMutation = useDeleteConfigurableType(category);

  const { data, isLoading, error } = useConfigurableTypes(category);
  const { data: dataById } = useConfigurableTypeById(category, urlId || '');
  const isSingleTypeView = Boolean(urlId && isCurrentTab && dataById && !error);
  const typesToProcess =
    isSingleTypeView && dataById ? [dataById] : (data ?? []);

  // Reset page when category changes
  useEffect(() => {
    setCurrentPage(1);
    setFilterValues({});
    setSystemFilter('all');
  }, [category]);

  // Client-side sort
  const sortedTypes = useMemo(() => {
    if (!typesToProcess.length && isLoading) return [];
    const rows = transformTypes(typesToProcess);
    if (!sortColumn || !sortDirection) return rows;
    return [...rows].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
      });
      return sortDirection === 'desc' ? -cmp : cmp;
    });
  }, [typesToProcess, isLoading, sortColumn, sortDirection]);
  // Client-side column filtering (applied after sort, before pagination)
  const filteredSortedTypes = useMemo(() => {
    let result = sortedTypes;

    // System/Custom toggle filter
    if (systemFilter === 'system') {
      result = result.filter((row) => row.is_system);
    } else if (systemFilter === 'custom') {
      result = result.filter((row) => !row.is_system);
    }

    // Column filters
    const activeFilters = Object.entries(filterValues).filter(([, v]) => v);
    if (activeFilters.length > 0) {
      result = result.filter((row) =>
        activeFilters.every(([col, val]) => {
          const cellValue = String(
            (row as Record<string, unknown>)[col] ?? '',
          ).toLowerCase();
          return cellValue.includes(val.toLowerCase());
        }),
      );
    }

    return result;
  }, [sortedTypes, filterValues, systemFilter]);

  const totalItems = filteredSortedTypes.length;
  const totalPages = useMemo(
    () => Math.ceil(totalItems / pageSize),
    [totalItems, pageSize],
  );
  const pagedTypes = useMemo(
    () =>
      filteredSortedTypes.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    [filteredSortedTypes, currentPage, pageSize],
  );

  const columns: Column<TypeRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('common.name', 'Name'),
        sortable: true,
        filterable: true,
        width: '180px',
      },
      {
        key: 'code',
        label: t('admin.code', 'Code'),
        sortable: true,
        filterable: true,
        width: '140px',
      },
      {
        key: 'description',
        label: t('forms.description', 'Description'),
        sortable: false,
      },
      {
        key: 'system_status',
        label: t('admin.system', 'System'),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t('admin.system', 'System'), value: 'info' },
          { label: t('admin.custom', 'Custom'), value: 'default' },
        ],
        width: '100px',
      },
      {
        key: 'active_status',
        label: t('common.active', 'Active'),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t('common.active', 'Active'), value: 'active' },
          { label: t('common.inactive', 'Inactive'), value: 'inactive' },
        ],
        width: '100px',
      },
      {
        key: 'sort_order',
        label: t('admin.sortOrder', 'Order'),
        sortable: true,
        width: '80px',
      },
    ],
    [t],
  );

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
    setCurrentPage(1);
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setActionMode('');
    setCurrentRow(undefined);
  }, []);

  const openAddDrawer = useCallback(() => {
    setActionMode('add');
    setCurrentRow(undefined);
    setDrawerOpen(true);
  }, []);

  const openEditDrawer = useCallback((row: TypeRow) => {
    setActionMode('edit');
    setCurrentRow(row);
    setDrawerOpen(true);
  }, []);

  const getInitialValues = useCallback((): ConfigurableTypeFormValues => {
    if (actionMode === 'edit' && currentRow && data) {
      const typeItem = data.find((t) => t.public_id === currentRow.id);
      if (typeItem) {
        return {
          name: typeItem.name,
          code: typeItem.code,
          description: typeItem.description,
          icon: typeItem.icon,
          color: typeItem.color,
          sort_order: typeItem.sort_order,
          is_active: typeItem.is_active,
        };
      }
    }
    return ConfigurableTypeInitialValues;
  }, [actionMode, currentRow, data]);

  const handleSubmit = (
    values: ConfigurableTypeFormValues,
    setError: UseFormSetError<ConfigurableTypeFormValues>,
  ) => {
    const payload: ConfigurableTypeWrite = {
      code: values.code,
      name: values.name,
      description: values.description || undefined,
      icon: values.icon || undefined,
      color: values.color || undefined,
      sort_order: values.sort_order === '' ? undefined : values.sort_order,
      is_active: values.is_active,
    };

    const handleApiError = (err: Error, label: string) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof ConfigurableTypeFormValues, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: label,
        description: err.message,
        type: 'error',
      });
    };

    if (actionMode === 'edit' && currentRow) {
      updateMutation.mutate(
        { id: currentRow.id, data: payload },
        {
          onSuccess: () => {
            toaster.create({ title: 'Type updated', type: 'success' });
            handleDrawerClose();
          },
          onError: (err) => handleApiError(err, 'Failed to update type'),
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toaster.create({ title: 'Type created', type: 'success' });
          handleDrawerClose();
        },
        onError: (err) => handleApiError(err, 'Failed to create type'),
      });
    }
  };

  const handleDelete = () => {
    if (!currentRow) return;
    deleteMutation.mutate(currentRow.id, {
      onSuccess: () => {
        toaster.create({ title: 'Type deleted', type: 'success' });
        handleDrawerClose();
      },
      onError: (err) => {
        toaster.create({
          title: 'Failed to delete type',
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const isSystemType = actionMode === 'edit' && currentRow?.is_system;
  const label = categoryLabels[category];
  return {
    // State
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    drawerOpen,
    actionMode,
    currentRow,
    filterValues,
    systemFilter,
    setSystemFilter,
    // Derived data
    isLoading,
    error,
    data,
    pagedTypes,
    totalItems,
    totalPages,
    columns,
    isSystemType,
    label,
    // Handlers
    handleSort,
    handleFilterChange,
    handleFilterClear,
    handleDrawerClose,
    openAddDrawer,
    openEditDrawer,
    handleSubmit,
    handleDelete,
    getInitialValues,
  };
}
